import { ZIndex } from "@/constants/z-index";
import { cx } from "@/utils/classnames";
import { useResizable } from "@/utils/hooks/use-resizable";
import { MOTION_DURATION_CLASS } from "@/utils/motion";
import { useAgentWorkspace } from "./AgentSidebarContext";
import { useFullPageConversationId } from "./use-full-page-conversation-id";
import React, { PropsWithChildren, useCallback, useLayoutEffect, useRef, useState } from "react";

export const ASSISTANT_RAIL_HEIGHT = 40;
const PANEL_GAP = 8;
// A chat opens at the default size and can be dragged out from its top and left edges; it does not
// go smaller than it opens. Width stops at the maximum or the canvas the rail spans, height a gap
// below the layout's top rail.
export const ASSISTANT_PANEL_DEFAULT_SIZE = { width: 420, height: 600 };
export const ASSISTANT_PANEL_MAX_WIDTH = 640;
// LayoutV2's top rail (`h-cds-40`).
const LAYOUT_TOP_RAIL_HEIGHT = 40;
// The top rail, the bottom rail and a gap beside each, which a panel's height never enters.
const PANEL_VERTICAL_INSET = LAYOUT_TOP_RAIL_HEIGHT + ASSISTANT_RAIL_HEIGHT + PANEL_GAP * 2;
// The rail paints over the panels, so a minimizing window finishes its shrink beneath its tab.
export const ASSISTANT_RAIL_Z_INDEX = ZIndex.NAVIGATION_OVERLAY + 2;
// The window at rest, and where a window without a measurable tab shrinks to: straight down
// under the rail.
const PANEL_REST_TRANSFORM = "translate(0px, 0px) scale(1, 1)";
const PANEL_FALLBACK_HIDDEN_TRANSFORM = `translateY(calc(100% + ${ASSISTANT_RAIL_HEIGHT + PANEL_GAP}px))`;
const formatTransformNumber = (value: number) => Math.round(value * 1000) / 1000;
const formatBoxTransform = (dx: number, dy: number, scaleX: number, scaleY: number) =>
  `translate(${formatTransformNumber(dx)}px, ${formatTransformNumber(dy)}px) ` +
  `scale(${formatTransformNumber(scaleX)}, ${formatTransformNumber(scaleY)})`;

// The rail control that stands for a chat: its tab, or "+ Assistant" for a blank chat.
const findChatTab = (chatId: string) =>
  document.querySelector<HTMLElement>(`[aria-controls="assistant-panel-${chatId}"]`);

/**
 * The transform that maps the panel's box, resting at `left`, onto its rail tab's; null when
 * either has no box because the tab is missing or the panel has not been laid out.
 */
const measureTabTransform = (chatId: string, panel: HTMLElement | null, left: number) => {
  const tab = findChatTab(chatId);
  const panelWidth = panel?.offsetWidth ?? 0;
  const panelHeight = panel?.offsetHeight ?? 0;
  if (tab == null || panelWidth === 0 || panelHeight === 0) return null;
  const tabRect = tab.getBoundingClientRect();
  const panelTop = window.innerHeight - (ASSISTANT_RAIL_HEIGHT + PANEL_GAP) - panelHeight;
  const dx = tabRect.left + tabRect.width / 2 - (left + panelWidth / 2);
  const dy = tabRect.top + tabRect.height / 2 - (panelTop + panelHeight / 2);
  return formatBoxTransform(dx, dy, tabRect.width / panelWidth, tabRect.height / panelHeight);
};

/**
 * The transform that maps the panel's box, resting at `left`, onto the window of chat `otherId`;
 * null when either has no box. Windows share a bottom edge, so only their horizontal centres and
 * heights differ.
 */
const measureWindowTransform = (otherId: string, panel: HTMLElement | null, left: number) => {
  const other = document.getElementById(`assistant-panel-${otherId}`);
  const panelWidth = panel?.offsetWidth ?? 0;
  const panelHeight = panel?.offsetHeight ?? 0;
  if (other == null || panelWidth === 0 || panelHeight === 0) return null;
  if (other.offsetWidth === 0 || other.offsetHeight === 0) return null;
  const dx = other.offsetLeft + other.offsetWidth / 2 - (left + panelWidth / 2);
  const dy = (panelHeight - other.offsetHeight) / 2;
  return formatBoxTransform(
    dx,
    dy,
    other.offsetWidth / panelWidth,
    other.offsetHeight / panelHeight,
  );
};

/**
 * The chat whose window this panel trades places with in the latest switch of the shown window,
 * or null when that change was no switch for it: a window opening or closing with none other
 * shown, or a switch between two other chats. `shownChatId` is the chat whose window is up.
 */
const useWindowSwitchPartner = (chatId: string, shownChatId: string | null) => {
  const [change, setChange] = useState<{ shown: string | null; previous: string | null }>({
    shown: shownChatId,
    previous: null,
  });
  let current = change;
  if (change.shown !== shownChatId) {
    current = { shown: shownChatId, previous: change.shown };
    setChange(current);
  }
  if (current.shown == null || current.previous == null) return null;
  if (current.shown === chatId) return current.previous;
  if (current.previous === chatId) return current.shown;
  return null;
};

/**
 * Where the window sits along the rail. Its right edge lines up with the right edge of its tab
 * (`tabRight`), and it is clamped to stay `PANEL_GAP` inside the rail. Without a measurable tab it
 * rests against the rail's right end.
 */
export const getAssistantPanelPlacement = (
  rail: { left: number; width: number },
  width: number,
  tabRight: number | null,
) => {
  const fitted = Math.max(0, Math.min(width, rail.width - PANEL_GAP * 2));
  const minLeft = rail.left + PANEL_GAP;
  const maxLeft = Math.max(minLeft, rail.left + rail.width - PANEL_GAP - fitted);
  const preferred = tabRight == null ? maxLeft : tabRight - fitted;
  return { width: fitted, left: Math.min(maxLeft, Math.max(minLeft, preferred)) };
};

/**
 * The right edge of the chat's rail tab, re-measured whenever the rail or its tabs may have moved
 * it; null while the tab has no box. A tab mid-entrance is zero-width but still has its height,
 * so it counts as measured.
 */
const useTabRight = (chatId: string, chats: unknown, railBounds: unknown) => {
  const [tabRight, setTabRight] = useState<number | null>(null);
  useLayoutEffect(() => {
    const measure = () => {
      const rect = findChatTab(chatId)?.getBoundingClientRect();
      const next = rect == null || (rect.width === 0 && rect.height === 0) ? null : rect.right;
      setTabRight((previous) => (previous === next ? previous : next));
    };
    measure();
    // A tab entering or leaving resizes the strip frame by frame, sliding its neighbours; tracking
    // it keeps the window riding along with its tab rather than jumping at the end.
    const strip = document.querySelector("[data-assistant-tab-strip]");
    const observer = new ResizeObserver(measure);
    if (strip != null) observer.observe(strip);
    window.addEventListener("resize", measure);
    // The tab strip scrolls horizontally once it overflows.
    document.addEventListener("scroll", measure, true);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
      document.removeEventListener("scroll", measure, true);
    };
  }, [chatId, chats, railBounds]);
  return tabRight;
};

const RESIZE_HANDLE_CLASS =
  "sql-explorer-resize-handle absolute z-20 bg-transparent focus-visible:outline-none";
// The grip's hover/drag/focus reveal comes from the SQL explorer's rules in styles/misc.css; the
// hairline itself takes the neutral tone of the tree connectors rather than the explorer's green.
// The edge grips stop short of the corner grip, so each hairline fades out from its centre
// towards the corners rather than ending in a hard stop.
const RESIZE_INDICATOR_CLASS =
  "sql-explorer-resize-indicator pointer-events-none absolute bg-zinc-400 dark:bg-white/[0.24]";
const TOP_INDICATOR_CLASS =
  "inset-x-0 top-0 h-px [mask-image:linear-gradient(to_right,transparent,black_25%,black_75%,transparent)]";
const LEFT_INDICATOR_CLASS =
  "inset-y-0 left-0 w-px [mask-image:linear-gradient(to_bottom,transparent,black_25%,black_75%,transparent)]";

export const ChalkAssistantPanel: React.FC<PropsWithChildren<{ chatId: string }>> = ({
  chatId,
  children,
}) => {
  const workspace = useAgentWorkspace();
  const state = workspace?.state;
  const railBounds = workspace?.railBounds ?? null;
  const updateChat = workspace?.updateChat;
  const chat = state?.chats.find((chat) => chat.id === chatId);
  // While a conversation's full page is up, its window is out of sight like a minimized one: the
  // page is the conversation.
  const fullPageConversationId = useFullPageConversationId();
  const railWidth = railBounds?.width ?? 0;
  const width = chat?.width ?? ASSISTANT_PANEL_DEFAULT_SIZE.width;
  const height = chat?.height ?? ASSISTANT_PANEL_DEFAULT_SIZE.height;
  const visible =
    chat != null &&
    !chat.minimized &&
    (fullPageConversationId == null || chat.conversationId !== fullPageConversationId) &&
    railBounds != null;
  // One window is up at a time (see `AssistantWorkspaceState`).
  const shownChatId =
    state?.chats.find(
      (chat) =>
        !chat.minimized &&
        (fullPageConversationId == null || chat.conversationId !== fullPageConversationId),
    )?.id ?? null;
  const switchPartnerId = useWindowSwitchPartner(chatId, shownChatId);
  // Tabs come, go, and change width with every chat's title and status, so any change to the
  // chats or the rail can shift this chat's tab. A minimized window keeps tracking its tab, so
  // restoring plays in place and its transcript keeps its layout meanwhile.
  const tabRight = useTabRight(chatId, state?.chats, railBounds);
  const placement = getAssistantPanelPlacement(
    { left: railBounds?.left ?? 0, width: railWidth },
    width,
    tabRight,
  );

  // The constraint callbacks are memoized because the hook re-subscribes its drag listeners when
  // they change, and the panel re-renders on every frame of a drag.
  const maxWidth = useCallback(
    () => Math.min(ASSISTANT_PANEL_MAX_WIDTH, railWidth - PANEL_GAP * 2),
    [railWidth],
  );
  const maxHeight = useCallback(() => window.innerHeight - PANEL_VERTICAL_INSET, []);
  // Sizes live on the chat so sibling panels re-flow live as this one grows into them. The hooks
  // own the pointer math and keyboard steps; "commit" mode stops them double-rendering per frame.
  const widthResize = useResizable({
    initialSize: width,
    externalSize: width,
    minSize: ASSISTANT_PANEL_DEFAULT_SIZE.width,
    maxSize: maxWidth,
    direction: "horizontal",
    anchor: "end",
    stateUpdateMode: "commit",
    onSizeChange: (next) => updateChat?.(chatId, { width: next }),
  });
  const heightResize = useResizable({
    initialSize: height,
    externalSize: height,
    minSize: ASSISTANT_PANEL_DEFAULT_SIZE.height,
    maxSize: maxHeight,
    direction: "vertical",
    anchor: "end",
    stateUpdateMode: "commit",
    onSizeChange: (next) => updateChat?.(chatId, { height: next }),
  });
  const widthContainerRef = widthResize.containerRef;
  const heightContainerRef = heightResize.containerRef;
  const panelRef = useRef<HTMLDivElement | null>(null);
  const setPanelRef = useCallback(
    (node: HTMLDivElement | null) => {
      panelRef.current = node;
      widthContainerRef.current = node;
      heightContainerRef.current = node;
    },
    [widthContainerRef, heightContainerRef],
  );
  const resizing = widthResize.isDragging || heightResize.isDragging;
  const left = placement.left;

  // Minimizing shrinks the window onto its rail tab and restoring grows it back out, so the hidden
  // transform maps the window's box onto the tab's. While hidden, the target is re-measured each
  // time the window's place moves, since the window follows its tab along the rail. Showing first
  // pins the window, with no transition, onto wherever its tab is now -- tabs may have moved without
  // moving the window -- and then releases it to rest, so the growth starts from the tab.
  //
  // Switching from one chat's window to another's instead plays as one window sliding along the
  // rail: the outgoing window slides onto the incoming one's place, and the incoming window slides
  // from the outgoing one's place while fading in above it, so the transcripts cross-fade. Only the
  // top window fades; fading both would let the page show through mid-switch.
  const [hiddenTransform, setHiddenTransform] = useState<string | null>(null);
  // The hidden transform as last applied, read when showing without re-running on its own change,
  // which would replace an outgoing slide with the tab target mid-switch.
  const hiddenTransformRef = useRef<string | null>(null);
  const wasVisibleRef = useRef(visible);
  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!visible) {
      const switchingOut = wasVisibleRef.current && switchPartnerId != null;
      wasVisibleRef.current = false;
      const measured =
        (switchingOut ? measureWindowTransform(switchPartnerId, panel, left) : null) ??
        measureTabTransform(chatId, panel, left);
      hiddenTransformRef.current = measured;
      setHiddenTransform((previous) => (previous === measured ? previous : measured));
      return;
    }
    if (wasVisibleRef.current) return;
    wasVisibleRef.current = true;
    if (panel == null) return;
    const fromWindow =
      switchPartnerId == null ? null : measureWindowTransform(switchPartnerId, panel, left);
    const from = fromWindow ?? measureTabTransform(chatId, panel, left);
    if (from == null || (fromWindow == null && from === hiddenTransformRef.current)) return;
    panel.style.transition = "none";
    panel.style.transform = from;
    if (fromWindow != null) panel.style.opacity = "0";
    // Reading layout commits the pinned style as the one the transition below starts from.
    void panel.offsetWidth;
    panel.style.transition = "";
    panel.style.transform = PANEL_REST_TRANSFORM;
    panel.style.opacity = "";
  }, [chatId, left, switchPartnerId, visible]);

  if (workspace == null) return null;
  const { focusChat } = workspace;

  return (
    <div
      ref={setPanelRef}
      role="region"
      aria-label={chat?.title ?? "Assistant chat"}
      data-assistant-panel={chatId}
      id={`assistant-panel-${chatId}`}
      aria-hidden={!visible}
      inert={!visible}
      data-ds-v2=""
      className={cx(
        "design-system-typography fixed overflow-hidden rounded-cds-6 shadow-cds-m dark:shadow-cds-l-dark",
        !resizing &&
          `transition-[left,width,height,transform,opacity,visibility] ${MOTION_DURATION_CLASS.short} ease-out motion-reduce:transition-none`,
      )}
      style={{
        // Preserve the transcript's layout and scroll geometry while minimized.
        visibility: visible ? "visible" : "hidden",
        // `visibility` rides along in the transition so the window stays painted until it has
        // shrunk onto its tab or slid onto the next window.
        transform: visible
          ? PANEL_REST_TRANSFORM
          : hiddenTransform ?? PANEL_FALLBACK_HIDDEN_TRANSFORM,
        contain: "strict",
        bottom: ASSISTANT_RAIL_HEIGHT + PANEL_GAP,
        left,
        width: placement.width,
        height: `min(${height}px, calc(100dvh - ${PANEL_VERTICAL_INSET}px))`,
        zIndex:
          state?.activeId === chatId ? ZIndex.NAVIGATION_OVERLAY + 1 : ZIndex.NAVIGATION_OVERLAY,
      }}
      onPointerDownCapture={() => {
        if (state?.activeId !== chatId) focusChat(chatId);
      }}
      onFocusCapture={() => {
        if (state?.activeId !== chatId) focusChat(chatId);
      }}
    >
      {children}
      <button
        type="button"
        aria-label="Resize chat height"
        className={cx(
          RESIZE_HANDLE_CLASS,
          "inset-x-4 top-0 h-2.5 cursor-ns-resize",
          heightResize.isDragging && "sql-explorer-resize-handle-active",
        )}
        onPointerDown={heightResize.handlePointerDown}
        onKeyDown={heightResize.handleKeyDown}
      >
        <span aria-hidden={true} className={cx(RESIZE_INDICATOR_CLASS, TOP_INDICATOR_CLASS)} />
      </button>
      <button
        type="button"
        aria-label="Resize chat width"
        className={cx(
          RESIZE_HANDLE_CLASS,
          "inset-y-4 left-0 w-2.5 cursor-ew-resize",
          widthResize.isDragging && "sql-explorer-resize-handle-active",
        )}
        onPointerDown={widthResize.handlePointerDown}
        onKeyDown={widthResize.handleKeyDown}
      >
        <span aria-hidden={true} className={cx(RESIZE_INDICATOR_CLASS, LEFT_INDICATOR_CLASS)} />
      </button>
      <button
        type="button"
        aria-label="Resize chat"
        className={cx(RESIZE_HANDLE_CLASS, "left-0 top-0 h-4 w-4 cursor-nwse-resize")}
        onPointerDown={(event) => {
          widthResize.handlePointerDown(event);
          heightResize.handlePointerDown(event);
        }}
        onKeyDown={(event) => {
          widthResize.handleKeyDown(event);
          heightResize.handleKeyDown(event);
        }}
      />
    </div>
  );
};
